import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Button, DatePicker, Spin } from "antd";
import Metric from "./Metric";
import moment from "moment";
import * as d3 from "d3";

import { ArrowLeftOutlined, ArrowRightOutlined, LoadingOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import {
  getAggMetrics,
  getFileImage,
  getUserByUserCode,
} from "../../utils/api";
import "./UserDetail.css";
import { registerMetricsHandler } from "../../utils/socket";
import PhoneHeartRate from "./PhoneHeartRate";
import PhoneHRV from "./PhoneHRV";
import dayjs from "dayjs";
import TimeAgo from "./TimeAgo";
import { useUserState } from "./UserState";
import MetricBar from "./MetricBar";
import { usePrevious } from "./PhoneHeartRateMiniMap";
import PhoneHRVminiMap from "./PhoneHRVminiMap";


const antIcon = (
  <LoadingOutlined style={{ color: "black", fontSize: 32 }} spin />
);

function UserDetail({ userCode, dashboardStore, orgDashboardStore }) {
  const [mode, setMode] = useState("live");
  const [metrics, setMetrics] = useState([]);
  const [oneDayMetrics, setOneDayMetrics] = useState([]);
  const [lastTimepoint, setLastTimepoint] = useState(new Date().toISOString());
  const [user, setUser] = useState({});
  const [profileImage, setProfileImageUrl] = useState();
  const [isZoomed, setIsZoomed] = useState(false);

  const [loading, setLoading] = useState(false);
  const [day, setDay] = useState(
    moment().format("YYYY-MM-DD")
  );


  const selectionDate = () => {
    const defaultSelectionstart = moment(day);
    const defaultSelectionEnd = moment(day).add(1, 'days');
    return [defaultSelectionstart, defaultSelectionEnd]
  }
  const [selection, setSelection] = useState(selectionDate);
  const previousSelection = usePrevious(selection);


  const onDayChange = (date, dateString) => {
    setDay(dateString);
    setMetrics([]);
    setOneDayMetrics([]);
    setIsZoomed(false);
  };

  const handleDayIncrease = () => {
    const date = moment(day);
    const newDate = moment(date).add(1, 'day').format("YYYY-MM-DD");
    setIsZoomed(false);
    setDay(newDate);
  }

  const handleDayDecrease = () => {
    const date = moment(day);
    const newDate = moment(date).subtract(1, 'day').format("YYYY-MM-DD");
    setIsZoomed(false);
    setDay(newDate);
  }

  function isTimeDiffGreaterThan60Seconds(timepoint1, timepoint2) {
    const diffInSeconds = (new Date(timepoint2) - new Date(timepoint1)) / 1000;
    return diffInSeconds >= 60;
  }

  const OnSocketMessage = useCallback((data) => {
    if (
      data.hasOwnProperty("metrics") && data.user_code === userCode
    ) {
      data = data["metrics"];
      const toAppend = [];
      data.forEach(d => {
        if (isTimeDiffGreaterThan60Seconds(lastTimepoint, d["timepoint"])) {
          setLastTimepoint(d["timepoint"]);
          toAppend.push(d);
          setMetrics((prevData) => prevData.concat(d));
        } else {
          metrics[metrics.length - 1] = d;
          setMetrics(metrics);
        }
      });
      console.log("on socket message", metrics.length);
    }
  }, [lastTimepoint, userCode, metrics])

  const onCLick1D = () => {
    setMode("1D");
    setIsZoomed(false);
    setDay(moment().format("YYYY-MM-DD"));
  }

  const fetchProfileData = async () => {
    const userDetail_ = getUserByUserCode(userCode);
    setUser(await userDetail_);
  };

  const getUTCOffset = () => {
    const now = new Date();
    const offset = -now.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  const fetchMetrics = useCallback(async () => {
    try{
      setLoading(true);
      if (day !== "" && isZoomed) {
        const metrics = await getAggMetrics(userCode, day + "T00:00:00" + getUTCOffset(), day + "T23:00:00" + getUTCOffset(), 1);
        setOneDayMetrics(metrics);
      console.log("Setting metrics zoom --- " + metrics.length);
      } else if (day !== "") {
        const metrics = await getAggMetrics(userCode, day + "T00:00:00" + getUTCOffset(), day + "T23:00:00" + getUTCOffset(), 10);
        setOneDayMetrics(metrics);
      console.log("Setting metrics day --- " + metrics.length);

      }
      const start = d3.min(metrics.map(x=>x.timepoint));
      const end = d3.max(metrics.map(x=>x.timepoint));
      // console.log(start, end);
      const selection = [moment(start).toDate(), moment(end).toDate()];
      setSelection(selection);
      setLoading(false);
    } catch(e) {
      console.error("--------------_", e);
      setOneDayMetrics([]);
      setLoading(false);
    }
  }, [day, isZoomed, userCode])


  useEffect(() => {
    if (mode === "1D") {
      fetchMetrics();
    }
  }, [userCode, mode, fetchMetrics]);

  useEffect(() => {
    if (mode === "live") {
      registerMetricsHandler(OnSocketMessage);
      setIsZoomed(false);
    }
  }, [userCode, mode, OnSocketMessage]);

  const fetchMetricsForLive = useCallback(async () => {
    setLoading(true);
    const now = new Date();
    const startTime = new Date(now);
    startTime.setMinutes(0, 0, 0);
    startTime.setMilliseconds(0);
    const endTime = new Date(now);
    endTime.setMinutes(59, 59, 999, 0);
    endTime.setMilliseconds(0);

    const startTimeString = startTime.toISOString().replace(".000", "");
    const endTimeString = endTime.toISOString().replace(".000", "");
    try {

      const metrics = await getAggMetrics(userCode, startTimeString, endTimeString, 1);
      metrics.map(m => {
        m.timepoint = m.time
        return m
      })
      setMetrics(metrics.reverse());
      setLoading(false);
    } catch(e) {
      setMetrics([]);
      setLoading(false);
    }
  }, [userCode]);

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCode])

  useEffect(() => {
    if(mode === "live") {
      fetchMetricsForLive();
    }
  }, [userCode, mode, fetchMetricsForLive])

  useEffect(() => {
    setSelection(selectionDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, userCode])

  useEffect(() => {
    const fetchImage = async () => {
      if (!user || !user.profile || !user.profile.photo) {
        return;
      }
      const image = await getFileImage(user.profile.photo);
      setProfileImageUrl(image.url);
    };
    fetchImage();
  }, [user]);

  const { heartRate, hrv, motion, color, alertLabel } = useUserState(
    orgDashboardStore,
    dashboardStore,
    userCode,
    true,
    true
  );

  return (
    <>
      <div className="UserDetail">
        <div className="UserDetail__profile_container">
          <div
            className="UserDetail__profile"
            style={{ backgroundImage: `url(${profileImage})` }}
          ></div>
          <div className="UserDetail__profile_detail">
            <div className={`UserDetail__status_bar ${color}`}></div>
            <div className="UserDetail__status_text">{alertLabel}</div>

            <Row style={{ clear: "both" }}>
              <Col sm={12} xs={24}>
                <div className="UserDetail__name_container">
                  <div className="UserDetail__name">{userCode} </div>
                  <div className="UserDetail__name_props">
                    {user?.profile?.phone && (
                      <span>ph. {user?.profile?.phone}</span>
                    )}
                  </div>
                  <div className="UserDetail__name_props">
                    {color.toUpperCase() !== "INACTIVE" && <TimeAgo
                      orgDashboardStore={orgDashboardStore}
                      userCode={userCode}
                    ></TimeAgo>}
                  </div>
                </div>
              </Col>
              <Col sm={12} xs={24}>
                <div style={{ float: "right" }}>
                  <Metric
                    label="Current Status"
                    unit=""
                    value={color.toUpperCase()}
                    color={color}
                    fontSize={"1.5rem"}
                  ></Metric>
                </div>

                <div className="clear"></div>
                <MetricBar
                  heartRate={heartRate}
                  hrv={hrv}
                  motion={motion}
                  orgDashboardStore={orgDashboardStore}
                  userCode={userCode}
                ></MetricBar>
              </Col>
            </Row>
          </div>
        </div>

        <div className="UserDetail__TimeSelection">
          <Button onClick={() => setMode("live")} disabled={mode === "live"}>Live</Button>&nbsp;
          <Button onClick={onCLick1D} disabled={mode === "1D"}>1D</Button>
          {
            mode === "1D" && (
              <div style={{ float: "right" }}>
                <Button onClick={handleDayDecrease} disabled={!day}>
                  <ArrowLeftOutlined />
                </Button>&nbsp;
                <DatePicker
                  onChange={onDayChange}
                  defaultValue={dayjs(day, "YYYY-MM-DD")}
                  value={day && dayjs(day, "YYYY-MM-DD")}
                />&nbsp;
                <Button onClick={handleDayIncrease} disabled={!day || day === moment().format("YYYY-MM-DD")}>
                  <ArrowRightOutlined />
                </Button>
                <div></div>
              </div>
            )
          }
        </div>

        {loading && <Spin style={{ marginLeft: "20px" }} indicator={antIcon} />}

        {!loading && ((
          (mode === "live" && metrics.length === 0) ||
          (mode === "1D" && oneDayMetrics.length === 0))
          && (
            <div className="UserDetail__no_data">
              No data available. {mode === "live" ? "Please switch to one day view." : "Please change the day filter."}
            </div>
          ))}

        {!loading && (
          (mode === "live" && metrics.length > 0) ||
          (mode === "1D" && oneDayMetrics.length > 0)
        ) && (
            <>
              <div className="UserDetail__TimeSelection"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                Heartrate / HRV
                {mode === "1D" && (!isZoomed ? (
                  <Button style={{ display: "inline" }} onClick={() => setIsZoomed(true)}>
                    <ZoomInOutlined />
                  </Button>
                ) : (
                  <Button style={{ display: "inline" }} onClick={() => setIsZoomed(false)}>
                    <ZoomOutOutlined />
                  </Button>
                ))}

              </div>

              {
                !isZoomed ? (
                  <>
                  <div>{oneDayMetrics.length}</div>
                    <PhoneHeartRate
                      data={mode === "live" ? metrics : oneDayMetrics}
                      day={day}
                      mode={mode}
                      style={{ width: "100%", height: "130px" }}
                    ></PhoneHeartRate>

                    <PhoneHRV
                      data={mode === "live" ? metrics : oneDayMetrics}
                      day={day}
                      mode={mode}
                      style={{ width: "100%", height: "130px" }}
                    ></PhoneHRV>
                  </>
                ) :
                  (
                    <>
                      <PhoneHRVminiMap
                        data={mode === "live" ? metrics : oneDayMetrics}
                        day={day}
                        mode={mode}
                        selection={selection}
                        setSelection={setSelection}
                        previousSelection={previousSelection}
                        style={{ width: "100%", height: "150px" }}
                      >

                        {selection =>
                          <>
                            <PhoneHeartRate
                              data={mode === "live" ? metrics : oneDayMetrics}
                              day={day}
                              mode={mode}
                              selection={selection}
                              style={{ width: "100%", height: "130px" }}
                            />
                            <PhoneHRV
                              data={mode === "live" ? metrics : oneDayMetrics}
                              day={day}
                              mode={mode}
                              selection={selection}
                              style={{ width: "100%", height: "130px" }}
                            />
                          </>
                        }
                      </PhoneHRVminiMap>
                    </>
                  )
              }
            </>
          )}
      </div>
    </>
  );
}

export default UserDetail;
